/**
 * BubbleManager.js
 * Manage bubble interactions
 */

import { Game } from "pixi_core";

import { Bubble } from "./Bubble";
import { Grid } from "./Grid";

import { GameEvents, BubbleColor, NB_MAX_COLUMN, NB_COLOR_MATCH } from "../Constants";

export class BubbleManager {
    /**
     * @public
     * @param {Grid} grid
     */
    constructor(grid) {
        /** @private @type {Grid} */
        this._grid = grid;
    }

    /**
     * Find a cluster of matching bubbles
     * @public
     * @param {Object} initialBubble - Object instead of bubble so random bubbles can be validated before creation
     * @param {BubbleColor} matchColor
     */
    findCluster(initialBubble, matchColor) {
        /** @type {Bubble[]} */
        let matchingBubbles = [];
        /** @type {Bubble[]} */
        let shieldedBubbles = [];

        let processed = [initialBubble];
        let opened = [initialBubble];
        initialBubble.processed = true;

        while (opened.length > 0) {
            /** @private @type {Bubble} */
            let bubble = opened.pop();

            if (
                (bubble.color === matchColor && bubble.specialType !== BubbleColor.eSHIELDED && bubble.color !== BubbleColor.eSTONE) ||
                bubble.color === BubbleColor.eMULTICOLOR ||
                bubble.color === BubbleColor.eLIGHTNING
            ) {
                matchingBubbles.push(bubble);

                /** @type {Bubble[]} */
                let neighbors = this._grid.getNeighborBubbles(bubble.column, bubble.row);

                for (const neighbor of neighbors) {
                    if (!neighbor.processed) {
                        neighbor.processed = true;
                        processed.push(neighbor);
                        opened.push(neighbor);
                    }
                }
            }
            if (bubble.specialType === BubbleColor.eSHIELDED) {
                shieldedBubbles.push(bubble);
            }
        }

        if (matchingBubbles.length >= NB_COLOR_MATCH) {
            for (const bubble of shieldedBubbles) {
                matchingBubbles.push(bubble);
            }
        }

        // Reset data
        for (const bubble of processed) {
            bubble.processed = false;
        }

        return matchingBubbles;
    }

    /**
     * On impact bubble effects
     * @public
     * @param {Bubble} initialBubble
     */
    applyImpactEffects(initialBubble) {
        const neighbors = this._grid.getNeighborBubbles(initialBubble.column, initialBubble.row);
        for (const bubble of neighbors) {
            // Shielded special
            if (bubble.specialType === BubbleColor.eSHIELDED) {
                bubble.pop();
            }
            // Colorless special
            if (bubble.color === BubbleColor.eCOLORLESS && initialBubble.color < BubbleColor.eSPECIAL) {
                Game.fire(GameEvents.eBUBBLE_COLORLESS_MATCHED, bubble, initialBubble.color);
            }
        }
    }

    /**
     * Find a cluster of matching bubbles in the initial grid
     * @public
     * @param {Bubble} initialBubble
     * @param {BubbleColor} matchColor
     */
    findInitialMatch(initialBubble, matchColor) {
        if (initialBubble.initialMatched) return false;
        const matchingBubbles = this.findCluster(initialBubble, matchColor);

        for (const bubble of matchingBubbles) {
            bubble.initialMatched = true;
            bubble.initialClusterSize = matchingBubbles.length;
        }

        return matchingBubbles;
    }

    /**
     * Find all disconnected bubbles (any bubble no longer connected to the first column)
     * @public
     * @returns {Bubble[]}
     */
    findDisconnectedBubbles() {
        let disconnectedBubbles = [];
        let processed = [];

        // First column of bubbles
        let opened = this._grid.getFirstColumnBubbles();
        for (const bubble of opened) {
            bubble.connected = true;
            processed.push(bubble);
        }

        // Flag bubbles connected to the first column
        while (opened.length > 0) {
            let bubble = opened.pop();

            /** @type {Bubble[]} */
            let neighbors = this._grid.getNeighborBubbles(bubble.column, bubble.row);

            for (const neighbor of neighbors) {
                if (!neighbor.connected) {
                    neighbor.connected = true;
                    processed.push(neighbor);
                    opened.push(neighbor);
                }
            }
        }

        // Find disconnected bubbles
        for (let column = 0; column < NB_MAX_COLUMN; ++column) {
            const maxRow = this._grid.getNbRows(column);
            for (let row = 0; row < maxRow; ++row) {
                let bubble = this._grid.getBubble(column, row);
                if (bubble && !bubble.connected) {
                    disconnectedBubbles.push(bubble);
                }
            }
        }

        // Reset data
        for (const bubble of processed) {
            bubble.connected = false;
        }

        return disconnectedBubbles;
    }

    /**
     * Create the damage events for the given cleared bubbles
     * @public
     * @param {Bubble[]} clearedBubbles
     * @param {Boolean} matching
     * @param {BubbleColor} matchColor
     */
    createDamageEvents(clearedBubbles, matching, matchColor) {
        if (matching) {
            Game.fire(GameEvents.eBUBBLE_CLEARED, matchColor, clearedBubbles.length);
            return;
        }

        let red = 0;
        let blue = 0;
        let orange = 0;
        let yellow = 0;
        let purple = 0;
        let green = 0;
        let special = 0;
        let stone = 0;
        let multicolor = 0;

        for (const bubble of clearedBubbles) {
            let color = bubble.color;

            if (color > BubbleColor.eSPECIAL && color !== BubbleColor.eSTONE) {
                color = BubbleColor.eSPECIAL;
            }

            switch (color) {
                case BubbleColor.eRED:
                    ++red;
                    break;
                case BubbleColor.eORANGE:
                    ++orange;
                    break;
                case BubbleColor.eYELLOW:
                    ++yellow;
                    break;
                case BubbleColor.eGREEN:
                    ++green;
                    break;
                case BubbleColor.eBLUE:
                    ++blue;
                    break;
                case BubbleColor.ePURPLE:
                    ++purple;
                    break;
                case BubbleColor.eSTONE:
                    ++stone;
                    break;
                case BubbleColor.eSPECIAL:
                    ++special;
                    break;
                case BubbleColor.eMULTICOLOR:
                    ++multicolor;
                    break;
            }
        }

        if (multicolor > 0) {
            if (red > 0) red += multicolor;
            if (orange > 0) orange += multicolor;
            if (yellow > 0) yellow += multicolor;
            if (green > 0) green += multicolor;
            if (blue > 0) blue += multicolor;
            if (purple > 0) purple += multicolor;
        }

        if (red > 0) {
            Game.fire(GameEvents.eBUBBLE_CLEARED, BubbleColor.eRED, red);
        }
        if (orange > 0) {
            Game.fire(GameEvents.eBUBBLE_CLEARED, BubbleColor.eORANGE, orange);
        }
        if (yellow > 0) {
            Game.fire(GameEvents.eBUBBLE_CLEARED, BubbleColor.eYELLOW, yellow);
        }
        if (green > 0) {
            Game.fire(GameEvents.eBUBBLE_CLEARED, BubbleColor.eGREEN, green);
        }
        if (blue > 0) {
            Game.fire(GameEvents.eBUBBLE_CLEARED, BubbleColor.eBLUE, blue);
        }
        if (purple > 0) {
            Game.fire(GameEvents.eBUBBLE_CLEARED, BubbleColor.ePURPLE, purple);
        }
        if (special > 0) {
            Game.fire(GameEvents.eBUBBLE_CLEARED, BubbleColor.eSPECIAL, special);
        }
        if (stone > 0) {
            Game.fire(GameEvents.eBUBBLE_CLEARED, BubbleColor.eSTONE, stone);
        }
    }
}
